import { Link } from 'react-router-dom'
import { useProjects } from '@/hooks/useProject'
import { useAuth } from '@/hooks/useAuth'
import { useState } from 'react'

const BRIDGENEXT_PURPLE = '#5F00AA'

interface CreateProjectModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (data: { name: string; acquiredCompanyName: string; description?: string }) => Promise<void>
}

function CreateProjectModal({ isOpen, onClose, onCreate }: CreateProjectModalProps) {
  const [formData, setFormData] = useState({ name: '', acquiredCompanyName: '', description: '' })
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await onCreate(formData)
      setFormData({ name: '', acquiredCompanyName: '', description: '' })
      onClose()
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Create New Project</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Acquired Company</label>
            <input
              type="text"
              value={formData.acquiredCompanyName}
              onChange={(e) => setFormData({ ...formData, acquiredCompanyName: e.target.value })}
              required
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2"
            ></textarea>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-white rounded"
              style={{ backgroundColor: BRIDGENEXT_PURPLE }}
            >
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { data: projects = [], isLoading } = useProjects()
  const { hasMinRole } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (isLoading) {
    return <div className="text-center py-8">Loading projects...</div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
        {hasMinRole('Administrator') && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 text-white rounded-lg font-medium"
            style={{ backgroundColor: BRIDGENEXT_PURPLE }}
          >
            New Project
          </button>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No projects yet</h2>
          <p className="text-gray-600 mb-6">Start your first IT acquisition project today</p>
          {hasMinRole('Administrator') && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-2 text-white rounded-lg font-medium"
              style={{ backgroundColor: BRIDGENEXT_PURPLE }}
            >
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-lg transition p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-1">{project.name}</h3>
              <p className="text-sm text-gray-600 mb-4">{project.acquiredCompanyName}</p>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Progress</span>
                  <span className="text-xs font-bold text-gray-900">{project.progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      backgroundColor: BRIDGENEXT_PURPLE,
                      width: `${project.progressPercent}%`,
                    }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>{project.memberCount} members</span>
                <span>{new Date(project.createdAt).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={async (data) => {
          console.log('Creating project:', data)
          // API call will be implemented
        }}
      />
    </div>
  )
}
